import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from './LanguageContext';
interface City {
  name: string;
  lat: number;
  lng: number;
  size: 'hq' | 'lg' | 'md' | 'sm';
  labelPos: 'top' | 'bottom' | 'left' | 'right';
}
const ORLANDO_LAT = 28.5383;
const ORLANDO_LNG = -81.3792;
const CITIES: City[] = [
// HQ
{
  name: 'Orlando HQ',
  lat: 28.5383,
  lng: -81.3792,
  size: 'hq',
  labelPos: 'bottom'
},
// Large cities
{
  name: 'Kissimmee',
  lat: 28.2919,
  lng: -81.4075,
  size: 'lg',
  labelPos: 'bottom'
}, {
  name: 'Sanford',
  lat: 28.8005,
  lng: -81.2731,
  size: 'lg',
  labelPos: 'top'
}, {
  name: 'Clermont',
  lat: 28.5494,
  lng: -81.7728,
  size: 'lg',
  labelPos: 'left'
}, {
  name: 'Daytona Beach',
  lat: 29.2108,
  lng: -81.0228,
  size: 'lg',
  labelPos: 'right'
}, {
  name: 'Lakeland',
  lat: 28.0394,
  lng: -81.9498,
  size: 'lg',
  labelPos: 'left'
},
// Medium cities
{
  name: 'Winter Park',
  lat: 28.5999,
  lng: -81.3395,
  size: 'md',
  labelPos: 'right'
}, {
  name: 'Lake Mary',
  lat: 28.7588,
  lng: -81.3178,
  size: 'md',
  labelPos: 'left'
}, {
  name: 'Apopka',
  lat: 28.6779,
  lng: -81.5115,
  size: 'md',
  labelPos: 'left'
}, {
  name: 'Ocoee',
  lat: 28.5733,
  lng: -81.5431,
  size: 'md',
  labelPos: 'left'
}, {
  name: 'Winter Garden',
  lat: 28.5652,
  lng: -81.5861,
  size: 'md',
  labelPos: 'bottom'
}, {
  name: 'Windermere',
  lat: 28.4955,
  lng: -81.5347,
  size: 'md',
  labelPos: 'left'
}, {
  name: 'Lake Nona',
  lat: 28.3732,
  lng: -81.2713,
  size: 'md',
  labelPos: 'right'
}, {
  name: 'St. Cloud',
  lat: 28.2489,
  lng: -81.2811,
  size: 'md',
  labelPos: 'right'
}, {
  name: 'Poinciana',
  lat: 28.1402,
  lng: -81.4584,
  size: 'md',
  labelPos: 'bottom'
}, {
  name: 'Altamonte Springs',
  lat: 28.6611,
  lng: -81.3656,
  size: 'md',
  labelPos: 'top'
}, {
  name: 'Casselberry',
  lat: 28.633,
  lng: -81.3231,
  size: 'md',
  labelPos: 'right'
}, {
  name: 'Longwood',
  lat: 28.703,
  lng: -81.3384,
  size: 'md',
  labelPos: 'left'
}, {
  name: 'Oviedo',
  lat: 28.67,
  lng: -81.2081,
  size: 'md',
  labelPos: 'right'
}, {
  name: 'Winter Springs',
  lat: 28.6986,
  lng: -81.3081,
  size: 'md',
  labelPos: 'top'
}, {
  name: 'Celebration',
  lat: 28.3183,
  lng: -81.54,
  size: 'md',
  labelPos: 'left'
}, {
  name: 'Avalon Park',
  lat: 28.5305,
  lng: -81.1639,
  size: 'md',
  labelPos: 'right'
}, {
  name: 'UCF Area',
  lat: 28.6024,
  lng: -81.2,
  size: 'md',
  labelPos: 'right'
},
// Small cities
{
  name: 'Maitland',
  lat: 28.6277,
  lng: -81.3628,
  size: 'sm',
  labelPos: 'left'
}, {
  name: 'Dr. Phillips',
  lat: 28.4516,
  lng: -81.4881,
  size: 'sm',
  labelPos: 'left'
}, {
  name: 'Hunters Creek',
  lat: 28.3622,
  lng: -81.425,
  size: 'sm',
  labelPos: 'bottom'
}, {
  name: 'Deltona',
  lat: 28.9005,
  lng: -81.2636,
  size: 'sm',
  labelPos: 'right'
}, {
  name: 'DeLand',
  lat: 29.0283,
  lng: -81.3031,
  size: 'sm',
  labelPos: 'top'
}, {
  name: 'Mount Dora',
  lat: 28.8013,
  lng: -81.6442,
  size: 'sm',
  labelPos: 'left'
}, {
  name: 'Leesburg',
  lat: 28.8108,
  lng: -81.8778,
  size: 'sm',
  labelPos: 'left'
}, {
  name: 'Haines City',
  lat: 28.1141,
  lng: -81.6278,
  size: 'sm',
  labelPos: 'bottom'
}, {
  name: 'Davenport',
  lat: 28.1614,
  lng: -81.6017,
  size: 'sm',
  labelPos: 'right'
}];
// Map dimensions & scaling — circle fills the entire viewBox
const RADIUS_PX = 480;
const PADDING = 40;
const MAP_WIDTH = (RADIUS_PX + PADDING) * 2;
const MAP_HEIGHT = (RADIUS_PX + PADDING) * 2;
const CENTER_X = MAP_WIDTH / 2;
const CENTER_Y = MAP_HEIGHT / 2;
// Scale so that 40 miles ≈ RADIUS_PX in SVG units
const SCALE_X = RADIUS_PX / 0.75;
const SCALE_Y = RADIUS_PX / 0.65;
function toSvgCoords(lat: number, lng: number): {
  x: number;
  y: number;
} {
  const x = CENTER_X + (lng - ORLANDO_LNG) * SCALE_X;
  const y = CENTER_Y - (lat - ORLANDO_LAT) * SCALE_Y;
  return {
    x,
    y
  };
}
export function CoverageMap() {
  const {
    t
  } = useLanguage();
  return <section id="coverage" className="py-24 bg-brand-black border-y border-white/5" data-id="element-60">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-61">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-62">
          <h2 className="font-oswald text-4xl md:text-5xl font-bold text-brand-red mb-4 uppercase tracking-wide" data-id="element-63">
            {t.coverage.title}
          </h2>
          <h3 className="text-2xl text-white font-medium mb-4" data-id="element-64">
            {t.coverage.subtitle}
          </h3>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-65">
            {t.coverage.description}
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="relative w-full rounded-2xl overflow-hidden border-2 border-brand-red/30 shadow-[0_0_40px_rgba(220,38,38,0.15)] bg-[#0A0A0A]" data-id="element-66">
          {/* Legend */}
          <div className="absolute top-6 left-6 z-20 bg-brand-black/95 backdrop-blur-md border border-white/10 px-5 py-4 rounded-xl shadow-xl" data-id="element-67">
            <div className="flex items-center gap-3 mb-3" data-id="element-68">
              <div className="w-3 h-3 rounded-full bg-brand-red animate-pulse shadow-[0_0_10px_rgba(220,38,38,0.8)]" data-id="element-69"></div>
              <span className="text-white font-oswald tracking-wide text-sm" data-id="element-70">
                {t.coverage.radius}
              </span>
            </div>
            <div className="flex items-center gap-3 mb-3" data-id="element-71">
              <div className="w-3 h-3 rounded-full bg-brand-yellow shadow-[0_0_10px_rgba(250,204,21,0.8)]" data-id="element-72"></div>
              <span className="text-gray-300 font-oswald tracking-wide text-sm" data-id="element-73">
                ORLANDO HQ
              </span>
            </div>
            <div className="flex items-center gap-3" data-id="element-74">
              <div className="w-3 h-3 rounded-full bg-white" data-id="element-75"></div>
              <span className="text-gray-400 font-oswald tracking-wide text-sm" data-id="element-76">
                MAJOR CITIES
              </span>
            </div>
          </div>

          {/* SVG Map */}
          <div className="w-full" style={{
          aspectRatio: '1 / 1'
        }} data-id="element-77">
            <svg viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`} width="100%" height="100%" preserveAspectRatio="xMidYMid meet" data-id="element-78">
              <defs data-id="element-79">
                <radialGradient id="coverageGlow" cx="50%" cy="50%" r="50%" data-id="element-80">
                  <stop offset="0%" stopColor="#DC2626" stopOpacity="0.15" data-id="element-81" />
                  <stop offset="60%" stopColor="#DC2626" stopOpacity="0.08" data-id="element-82" />
                  <stop offset="100%" stopColor="#DC2626" stopOpacity="0" data-id="element-83" />
                </radialGradient>
                <pattern id="dots" width="30" height="30" patternUnits="userSpaceOnUse" data-id="element-84">
                  <circle cx="2" cy="2" r="1.5" fill="rgba(255,255,255,0.05)" data-id="element-85" />
                </pattern>
              </defs>

              {/* Background dots */}
              <rect width={MAP_WIDTH} height={MAP_HEIGHT} fill="url(#dots)" data-id="element-86" />

              {/* Coverage circle glow */}
              <circle cx={CENTER_X} cy={CENTER_Y} r={RADIUS_PX + 30} fill="url(#coverageGlow)" data-id="element-87" />

              {/* Distance Rings */}
              {[{
              ratio: 0.3,
              label: '~12 MI'
            }, {
              ratio: 0.6,
              label: '~25 MI'
            }, {
              ratio: 1.0,
              label: '40 MI MAX RADIUS'
            }].map((ring, i) => {
              const r = RADIUS_PX * ring.ratio;
              const isOuter = i === 2;
              return <g key={`ring-${i}`} data-id="element-88">
                    <circle cx={CENTER_X} cy={CENTER_Y} r={r} fill={isOuter ? 'rgba(220, 38, 38, 0.03)' : 'none'} stroke={isOuter ? '#DC2626' : 'rgba(255,255,255,0.1)'} strokeWidth={isOuter ? '2.5' : '1'} strokeDasharray={isOuter ? '10 7' : '4 8'} opacity={isOuter ? '0.7' : '1'} data-id="element-89" />
                    <text x={CENTER_X} y={CENTER_Y - r - 10} textAnchor="middle" fill={isOuter ? '#DC2626' : 'rgba(255,255,255,0.3)'} fontSize={isOuter ? '20' : '16'} fontWeight="bold" fontFamily="'Oswald', sans-serif" letterSpacing="1.5" paintOrder="stroke fill" stroke="#0A0A0A" strokeWidth="4" strokeLinejoin="round" data-id="element-90">
                      {ring.label}
                    </text>
                  </g>;
            })}

              {/* City dots and labels */}
              {CITIES.map((city, idx) => {
              const pos = toSvgCoords(city.lat, city.lng);
              const isTampa = city.size === 'hq';
              const isLarge = city.size === 'lg';
              const isMedium = city.size === 'md';
              const dotRadius = isTampa ? 14 : isLarge ? 10 : isMedium ? 7 : 5;
              const fontSize = isTampa ? 32 : isLarge ? 26 : isMedium ? 20 : 17;
              const dotColor = isTampa ? '#FACC15' : isLarge ? '#FFFFFF' : '#DC2626';
              const textColor = isTampa ? '#FACC15' : isLarge ? '#FFFFFF' : isMedium ? '#E5E7EB' : '#9CA3AF';
              let textX = pos.x;
              let textY = pos.y;
              let textAnchor = 'middle';
              switch (city.labelPos) {
                case 'top':
                  textY = pos.y - dotRadius - 10;
                  break;
                case 'bottom':
                  textY = pos.y + dotRadius + 20 + (isTampa ? 4 : 0);
                  break;
                case 'left':
                  textX = pos.x - dotRadius - 10;
                  textY = pos.y + fontSize / 3;
                  textAnchor = 'end';
                  break;
                case 'right':
                  textX = pos.x + dotRadius + 10;
                  textY = pos.y + fontSize / 3;
                  textAnchor = 'start';
                  break;
              }
              return <g key={idx} data-id="element-91">
                    {/* Tampa HQ Pulse */}
                    {isTampa && <>
                        <circle cx={pos.x} cy={pos.y} r="18" fill="none" stroke="#FACC15" strokeWidth="2" opacity="0.4" data-id="element-92">
                          <animate attributeName="r" values="18;40;18" dur="2s" repeatCount="indefinite" data-id="element-93" />
                          <animate attributeName="opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" data-id="element-94" />
                        </circle>
                        <circle cx={pos.x} cy={pos.y} r="28" fill="none" stroke="#FACC15" strokeWidth="1" opacity="0.2" data-id="element-95">
                          <animate attributeName="r" values="28;50;28" dur="2.5s" repeatCount="indefinite" data-id="element-96" />
                          <animate attributeName="opacity" values="0.3;0;0.3" dur="2.5s" repeatCount="indefinite" data-id="element-97" />
                        </circle>
                      </>}

                    {/* City dot */}
                    <circle cx={pos.x} cy={pos.y} r={dotRadius} fill={dotColor} stroke="#0A0A0A" strokeWidth="2" data-id="element-98" />

                    {/* City name label */}
                    <text x={textX} y={textY} textAnchor={textAnchor} fill={textColor} fontSize={fontSize} fontWeight={isTampa || isLarge ? 'bold' : '600'} fontFamily="'Oswald', sans-serif" letterSpacing="0.5" paintOrder="stroke fill" stroke="#0A0A0A" strokeWidth={isTampa ? '6' : '5'} strokeLinejoin="round" style={{
                  textTransform: 'uppercase'
                }} data-id="element-99">
                      {city.name}
                    </text>
                  </g>;
            })}
            </svg>
          </div>
        </motion.div>
      </div>
    </section>;
}